import React, { useEffect, useState, useMemo } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Modal, Form, Input, Table, Button } from "antd";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { openTutorial } from "../../../stores/nutstat";
import {
  fetchStudents,
  downloadExcelStudentNutrition,
} from "../../../stores/nutrition";
import { responsive } from "../../../utils/style";
import PaperIcon from "../../../assets/icons/paper.svg";
import NutStat from "../../../components/Nutstat";
import {
  FieldContainer,
  CustomSelect,
  Option,
} from "../../../components/CustomFields";
import SearchIcon from "../../../assets/icons/search.svg";
import EditIcon from "../../../assets/icons/edit.svg";
import downloadIcon from "../../../assets/icons/downloadBtnIcon.svg";

import {
  HEIGHT_FOR_AGE_OPTIONS,
  WEIGHT_FOR_HEIGHT_OPTIONS,
} from "../../../utils/nutrition";

const COLUMNS = [
  {
    title: "ชื่อ (ชื่อเล่น)",
    dataIndex: "name",
    key: "name",
    width: "140px",
    align: "center",
  },
  {
    title: "น้ำหนัก",
    key: "weight",
    width: "80px",
    align: "center",
    render: (_, record) => (record.weight ? `${record.weight} กก.` : null),
  },
  {
    title: "ส่วนสูง",
    key: "height",
    width: "80px",
    align: "center",
    render: (_, record) => (record.height ? `${record.height} ซม.` : null),
  },
  {
    title: "ผลการประเมินส่วนสูง",
    key: "heightForAge",
    align: "center",
    render: (_, record) =>
      HEIGHT_FOR_AGE_OPTIONS.find((d) => d.value === record.heightForAge)
        ?.label,
  },
  {
    title: "ผลการประเมินน้ำหนัก",
    key: "weightForHeight",
    align: "center",
    render: (_, record) =>
      WEIGHT_FOR_HEIGHT_OPTIONS.find((d) => d.value === record.weightForHeight)
        ?.label,
  },
  {
    title: "",
    key: "action",
    align: "center",
    width: "50px",
    render: (_, record) => (
      <CustomEditBtn
        to={`/nutrition/form/${record.step}?studentId=${record.userId}`}
      >
        <Image src={EditIcon} />
      </CustomEditBtn>
    ),
  },
];

const Nutrition = () => {
  const dispatch = useDispatch();

  const { students } = useSelector((state) => state.nutrition);
  const { showTutorial } = useSelector((state) => state.nutstat);
  const { roomUnit = 0 } = useSelector((s) => s.auth.userInfo);

  const [data, setData] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  const [form] = Form.useForm();
  const { step } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const classNo = searchParams.get("classNo");
  const roomNo = searchParams.get("roomNo");

  useEffect(() => {
    // For show Tutorial modal
    const hideTutorial = localStorage.getItem("hideTutorial");
    if (hideTutorial !== "true") dispatch(openTutorial());
  }, []);

  useMemo(() => {
    setData(students);
  }, [students]);

  useEffect(() => {
    dispatch(fetchStudents({ classNo, roomNo, step }));
    if (classNo && roomNo) setShowSearch(true);
  }, [classNo, roomNo]);

  const handleChange = () => {
    const values = form.getFieldsValue(true);
    const filterParams = Object.entries(values).reduce(
      (a, [k, v]) => (v ? ((a[k] = v), a) : a),
      {}
    );
    const params = new URLSearchParams(filterParams);
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  const handleSearch = (e) => {
    if (e.target.value) {
      const regex = new RegExp(e.target.value);
      const filterData = data.filter((d) => regex.test(d.name));
      setData(filterData);
    } else {
      setData(students);
    }
  };

  const handleDownload = () => {
    dispatch(downloadExcelStudentNutrition({ data, columns: COLUMNS }));
  };

  return (
    <Container>
      <Background>
        <Modal visible={showTutorial} footer={null} closable={false}>
          <NutStat isStudent />
        </Modal>
        <Content>
          <Topic>กรอกข้อมูลภาวะโภชนาการ</Topic>
          <Inform>
            <Howto onClick={() => dispatch(openTutorial())}>
              <Image src={PaperIcon} />
              ดูวิธีเช็กภาวะโภชนาการ
            </Howto>

            <DownloadBtn hidden={data.length === 0} onClick={handleDownload}>
              <Image src={downloadIcon} style={{ marginRight: "8px" }} />
              ดาวน์โหลด
            </DownloadBtn>
          </Inform>
          <SubTopic>ข้อมูลภาวะโภชนาการ</SubTopic>
          <Form
            form={form}
            name="nutritionViews"
            layout="vertical"
            autoComplete="off"
            initialValues={{
              classNo,
              roomNo,
            }}
          >
            <GroupField>
              <FieldContainer name="classNo" label="ชั้น" marginBottom={0}>
                <CustomSelect
                  placeholder="โปรดระบุ"
                  allowClear
                  width="100%"
                  widthRs="100%"
                  onChange={handleChange}
                >
                  {[...Array(6).keys()].map((i) => (
                    <Option key={i} value={i + 1}>
                      ม.{i + 1}
                    </Option>
                  ))}
                </CustomSelect>
              </FieldContainer>
              <FieldContainer name="roomNo" label="ห้อง" marginBottom={0}>
                <CustomSelect
                  placeholder="โปรดระบุ"
                  allowClear
                  width="100%"
                  widthRs="100%"
                  onChange={handleChange}
                >
                  {[...Array(roomUnit).keys()].map((i) => (
                    <Option key={i} value={i + 1}>
                      {i + 1}
                    </Option>
                  ))}
                </CustomSelect>
              </FieldContainer>
            </GroupField>
            {showSearch && (
              <FieldContainer name="name" marginBottom={0}>
                <Input
                  placeholder="ระบุชื่อเพื่อค้นหา"
                  type="text"
                  onChange={handleSearch}
                />
                <SearchImage src={SearchIcon} />
              </FieldContainer>
            )}
          </Form>
        </Content>
        <TableContent>
          <Table columns={COLUMNS} dataSource={data} size="small" bordered />
        </TableContent>
      </Background>
    </Container>
  );
};

export default Nutrition;

const Container = styled.div`
  width: 100%;
  font-family: Noto Sans Thai, Noto Sans;
  background-color: #fffbeb;

  ${responsive.mobile(css`
    input {
      max-width: none;
    }
  `)}
`;
const Background = styled.div`
  width: 100%;
  min-height: 100vh;
  max-width: 1144px;
  background-color: white;
  padding: 40px 187px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${responsive.tablet(css`
    padding: 40px 24px;
    max-width: unset;
  `)}

  ${responsive.mobile(css`
    padding: 24px;
  `)}
`;
const Content = styled.div`
  max-width: 440px;
  width: 100%;

  form {
    .ant-form-item {
      display: grid;
    }

    input {
      border-radius: 4px;
      height: 52px;
      font-size: 18px;
      margin-bottom: 24px;

      ${responsive.mobile(css`
        margin-bottom: 16px;
      `)}
    }
  }
`;
const TableContent = styled.div`
  max-width: 650px;
  width: 100%;

  table {
    border: 1px solid !important;
    border-radius: 8px 8px 0 0;
  }

  table > thead > tr > th {
    font-size: 12px;

    &:first-child {
      border-radius: 8px 0 0 0 !important;
    }

    &:nth-last-child(1) {
      border-radius: 0 8px 0 0 !important;
    }
  }
`;
const GroupField = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;
const Topic = styled.h5`
  font-style: normal;
  font-weight: bold;
  font-size: 28px;
  line-height: 34px;
  letter-spacing: -0.02em;
  color: #058459;
  text-align: center;
  margin-bottom: 46px;
`;
const Inform = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 770px;
  margin-bottom: 35px;
  width: 100%;
`;
const Howto = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 20px;
  color: #4b5768;

  &:hover {
    cursor: pointer;
  }
`;
const Image = styled.img``;
const SubTopic = styled.p`
  font-weight: 500;
  font-size: 21px;
  line-height: 23px;
  letter-spacing: -0.02em;
  color: #4b5768;
  align-self: flex-start;
`;
const SearchImage = styled(Image)`
  position: absolute;
  right: 10px;
  top: 15px;
`;
const CustomEditBtn = styled(Link)`
  width: 42px;
  height: 42px;
  background: #16af7b;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const DownloadBtn = styled(Button)`
  cursor: pointer;
  font-family: "Noto Sans Thai";
  font-style: normal;
  font-weight: 600;
  font-size: 19px;
  line-height: 23px;
  /* identical to box height, or 121% */

  letter-spacing: -0.02em;

  /* Primary/Green 400 */

  color: #16af7b;
  padding: 14px 31px 35px;

  /* Primary/Green 50 */

  background: #b5f6df;
  /* Primary/Green 400 */

  border: 1px solid #16af7b;
  border-radius: 100px;
  float: right;

  :hover,
  :focus,
  :active {
    border: 1px solid #16af7b;
    color: #16af7b;
    background: #b5f6df;
  }

  ${responsive.mobile(css`
    padding: 14px 14px 32px;
  `)}
`;
